import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import {
  Card,
  Button,
  Space,
  Row,
  Col,
  Statistic,
  Tabs,
  message,
  Modal,
  Form,
  Upload,
  Input,
  Table,
  Select,
  DatePicker,
  Tag,
} from "antd";
import {
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  UploadOutlined,
  InboxOutlined,
  FileTextOutlined,
  SwapOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../components/AgGridWithColumnState";
import ResizableGridContainer from "../Shared/Utils/ResizableGridContainer";
import {
  getAllInvoicesParallel,
  getAllCreditsParallel,
  getCreditById,
  getInvoiceById,
  deleteInvoice,
  deleteCredit,
  uploadBillsFile,
  exportInvoicePDF,
  exportCreditPDF,
  approveInvoice,
  rejectInvoice,
  approveCredit,
  rejectCredit,
  getAccountsReceivableStatistics,
  clearAccountingCache,
} from "../../api/accounting";
import { clearOverpaymentCache } from "../../api/overpayment";
import {
  getCachedData,
  setCachedData,
  invalidateCacheByPrefix,
  CacheKeys,
  CacheTTL,
} from "../../context/AccountingDataCache";
import InvoiceHistory from "../Shared/Modals/InvoiceHistory";
import UpcomingInvoices from "../Reports/UpcomingInvoices";
import {
  getInvoiceColumns,
  getCreditNoteColumns,
  getHistoryColumns,
} from "./agGridColumns";
import CreateInvoiceModal from "../Shared/Forms/CreateInvoiceModal";
import CreateCredit from "../Shared/Forms/CreateCredit";
import InvoiceViewModal from "../Shared/Modals/InvoiceViewModal";
import ApprovalModal from "../Shared/Modals/ApprovalModal";
import BatchUploadBillsInvoices from "../Shared/Utils/BatchUploadBillsInvoices";
import BatchEditModal from "../Shared/Modals/BatchEditModal";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import { secureExportToExcel } from "../../../../utils/secureExcelUtils";

const { Option } = Select;
const { RangePicker } = DatePicker;

const EMPTY_ARRAY = [];

const AccountsReceivable = ({ selectedBrands: selectedBrandsProp = [] }) => {
  const profile = useSelector((state) => state.profile, shallowEqual);
  const profileBrands =
    useSelector(
      (state) => state.profile?.brands ?? EMPTY_ARRAY,
      shallowEqual
    ) ?? EMPTY_ARRAY;
  const navigate = useNavigate();

  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const {
    refreshKey: currencyRefreshKey,
    displayCurrency,
    displaySymbol,
    exchangeRate,
    isConverted,
    decimalPrecision,
  } = useDisplayCurrency();

  // Listen for global brand/client changes to invalidate cache and trigger refresh
  useEffect(() => {
    const handleGlobalBrandChanged = () => {
      // Invalidate all receivable caches when brand changes
      invalidateCacheByPrefix("invoices_");
      invalidateCacheByPrefix("invoice_credits_");
      invalidateCacheByPrefix("invoice_history_");
      invalidateCacheByPrefix("receivable_statistics_");
    };

    const handleGlobalClientChanged = () => {
      // When client changes, brands will also change, so invalidate everything
      invalidateCacheByPrefix("invoices_");
      invalidateCacheByPrefix("invoice_credits_");
      invalidateCacheByPrefix("invoice_history_");
      invalidateCacheByPrefix("receivable_statistics_");
    };

    const handleInvoiceCreated = () => {
      // Invalidate cache and refresh when an invoice is created elsewhere
      invalidateCacheByPrefix("invoices_");
      invalidateCacheByPrefix("invoice_credits_");
      invalidateCacheByPrefix("invoice_history_");
      invalidateCacheByPrefix("receivable_statistics_");
      // Trigger refresh
      if (fetchInvoicesDataRef.current) fetchInvoicesDataRef.current(true);
      if (fetchCreditNotesDataRef.current) fetchCreditNotesDataRef.current(true);
      if (fetchHistoryDataRef.current) fetchHistoryDataRef.current(true);
      if (fetchStatisticsRef.current) fetchStatisticsRef.current(true);
    };

    const handleCreditNoteCreated = (event) => {
      // Only refresh if it's an invoice credit (credit_type === "invoice")
      const creditType = event?.detail?.creditType;
      if (creditType === "invoice") {
        // Invalidate cache and refresh when an invoice credit note is created elsewhere
        invalidateCacheByPrefix("invoice_credits_");
        invalidateCacheByPrefix("invoices_");
        invalidateCacheByPrefix("invoice_history_");
        invalidateCacheByPrefix("receivable_statistics_");
        // Trigger refresh
        if (fetchInvoicesDataRef.current) fetchInvoicesDataRef.current(true);
        if (fetchCreditNotesDataRef.current) fetchCreditNotesDataRef.current(true);
        if (fetchHistoryDataRef.current) fetchHistoryDataRef.current(true);
        if (fetchStatisticsRef.current) fetchStatisticsRef.current(true);
      }
    };

    window.addEventListener("globalBrandChanged", handleGlobalBrandChanged);
    window.addEventListener("globalClientChanged", handleGlobalClientChanged);
    window.addEventListener("invoiceCreated", handleInvoiceCreated);
    window.addEventListener("creditNoteCreated", handleCreditNoteCreated);

    return () => {
      window.removeEventListener(
        "globalBrandChanged",
        handleGlobalBrandChanged
      );
      window.removeEventListener(
        "globalClientChanged",
        handleGlobalClientChanged
      );
      window.removeEventListener("invoiceCreated", handleInvoiceCreated);
      window.removeEventListener("creditNoteCreated", handleCreditNoteCreated);
    };
  }, []);

  // Use useMemo to prevent selectedBrands from changing on every render
  const selectedBrands = useMemo(() => {
    if (selectedBrandsProp && selectedBrandsProp.length > 0) {
      return selectedBrandsProp;
    }

    return profileBrands;
  }, [selectedBrandsProp, profileBrands]);

  const brandsKey = useMemo(() => {
    if (!selectedBrands || selectedBrands.length === 0) return "";
    return selectedBrands
      .map((brand) => brand.id)
      .filter((id) => id !== undefined && id !== null)
      .sort()
      .join(",");
  }, [selectedBrands]);

  // CRITICAL: Check global cache IMMEDIATELY to restore data instantly on navigation
  const initialInvoicesCache = useMemo(() => {
    if (!brandsKey) return null;
    return getCachedData(CacheKeys.invoices(brandsKey));
  }, [brandsKey]);

  const initialCreditsCache = useMemo(() => {
    if (!brandsKey) return null;
    return getCachedData(CacheKeys.invoiceCredits(brandsKey));
  }, [brandsKey]);

  // For components that need a single brand, use the first one
  const firstBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;

  // Fallback to sessionStorage if Redux profile not loaded yet
  const userRole = profile?.role || sessionStorage.getItem("user_role") || null;

  // Initialize state from cache - instant restore on navigation
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(() => initialInvoicesCache || []);
  const [creditNotesData, setCreditNotesData] = useState(
    () => initialCreditsCache || []
  );
  const [historyData, setHistoryData] = useState([]);
  const [statistics, setStatistics] = useState({
    totalOutstanding: 0,
    overdueAmount: 0,
    collectedThisMonth: 0,
    totalInvoices: 0,
    totalCredits: 0,
    pendingPayments: 0,
    unallocatedCredits: 0,
  });

  // AG Grid API refs for server-side model
  const invoicesGridApiRef = React.useRef(null);
  const creditsGridApiRef = React.useRef(null);
  // CRITICAL: Refs for preserving filter state across refreshes
  const savedInvoicesFilterRef = React.useRef(null);
  const savedCreditsFilterRef = React.useRef(null);
  const pendingInvoicesFilterRestoreRef = React.useRef(false);
  const pendingCreditsFilterRestoreRef = React.useRef(false);
  // CRITICAL: Refs for preserving sort state across refreshes
  const savedInvoicesSortRef = React.useRef(null);
  const savedCreditsSortRef = React.useRef(null);
  const pendingInvoicesSortRestoreRef = React.useRef(false);
  const pendingCreditsSortRestoreRef = React.useRef(false);
  // Ref for debouncing refresh calls to prevent multiple rapid refreshes
  const refreshTimeoutRef = React.useRef(null);
  const cachedDataRef = useRef(null);
  // Separate refs to allow parallel fetching of invoices and credits
  const fetchingInvoicesRef = useRef(false);
  const fetchingCreditsRef = useRef(false);
  const fetchInvoicesDataRef = useRef(null);
  const fetchCreditNotesDataRef = useRef(null);
  const fetchHistoryDataRef = useRef(null);
  const fetchStatisticsRef = useRef(null);
  // Track last fetched brands to prevent refetching on pagination
  const lastFetchedInvoicesBrandsKey = useRef(
    initialInvoicesCache ? brandsKey : null
  );
  const lastFetchedCreditsBrandsKey = useRef(
    initialCreditsCache ? brandsKey : null
  );

  const [activeTab, setActiveTab] = useState(() => {
    // Restore active tab from localStorage
    return (
      localStorage.getItem("accounts-receivable-active-tab") || "dashboard"
    );
  });
  const [createInvoiceModalVisible, setCreateInvoiceModalVisible] =
    useState(false);
  const [batchUploadInvoicesVisible, setBatchUploadInvoicesVisible] = useState(false);
  const [createCreditModalVisible, setCreateCreditModalVisible] =
    useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editingCreditId, setEditingCreditId] = useState(null);
  const [viewInvoiceModalVisible, setViewInvoiceModalVisible] = useState(false);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);
  const [viewingCreditId, setViewingCreditId] = useState(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  const [paymentsModalVisible, setPaymentsModalVisible] = useState(false);
  const [selectedInvoiceForPayments, setSelectedInvoiceForPayments] =
    useState(null);

  // Approval modal state
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [selectedItemForApproval, setSelectedItemForApproval] = useState(null);
  const [approvalItemType, setApprovalItemType] = useState("invoice"); // 'invoice' or 'credit'

  // Bulk approval state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [selectedCreditIds, setSelectedCreditIds] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [batchEditModalVisible, setBatchEditModalVisible] = useState(false);

  useEffect(() => {
    if (invoicesGridApiRef.current) {
      invoicesGridApiRef.current.refreshCells({ force: true });
    }
  }, [selectedInvoiceIds]);

  useEffect(() => {
    if (creditsGridApiRef.current) {
      creditsGridApiRef.current.refreshCells({ force: true });
    }
  }, [selectedCreditIds]);

  // Effect to restore invoices filter AFTER data has loaded
  useEffect(() => {
    if (
      pendingInvoicesFilterRestoreRef.current &&
      !loading &&
      data.length > 0 &&
      savedInvoicesFilterRef.current &&
      Object.keys(savedInvoicesFilterRef.current).length > 0
    ) {
      const restoreFilters = (attempt = 1) => {
        setTimeout(() => {
          if (invoicesGridApiRef.current) {
            try {
              invoicesGridApiRef.current.setFilterModel(
                savedInvoicesFilterRef.current
              );
              pendingInvoicesFilterRestoreRef.current = false;
            } catch (e) {
              if (attempt < 5) restoreFilters(attempt + 1);
              else pendingInvoicesFilterRestoreRef.current = false;
            }
          } else if (attempt < 5) {
            restoreFilters(attempt + 1);
          } else {
            pendingInvoicesFilterRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreFilters();
    }
  }, [loading, data.length]);

  // Effect to restore credits filter AFTER data has loaded
  useEffect(() => {
    if (
      pendingCreditsFilterRestoreRef.current &&
      !loading &&
      creditNotesData.length > 0 &&
      savedCreditsFilterRef.current &&
      Object.keys(savedCreditsFilterRef.current).length > 0
    ) {
      const restoreFilters = (attempt = 1) => {
        setTimeout(() => {
          if (creditsGridApiRef.current) {
            try {
              creditsGridApiRef.current.setFilterModel(
                savedCreditsFilterRef.current
              );
              pendingCreditsFilterRestoreRef.current = false;
            } catch (e) {
              if (attempt < 5) restoreFilters(attempt + 1);
              else pendingCreditsFilterRestoreRef.current = false;
            }
          } else if (attempt < 5) {
            restoreFilters(attempt + 1);
          } else {
            pendingCreditsFilterRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreFilters();
    }
  }, [loading, creditNotesData.length]);

  // Effect to restore invoices sort AFTER data has loaded
  useEffect(() => {
    if (
      pendingInvoicesSortRestoreRef.current &&
      !loading &&
      data.length > 0 &&
      savedInvoicesSortRef.current &&
      savedInvoicesSortRef.current.length > 0
    ) {
      const restoreSort = (attempt = 1) => {
        setTimeout(() => {
          if (invoicesGridApiRef.current) {
            try {
              invoicesGridApiRef.current.applyColumnState({
                state: savedInvoicesSortRef.current,
                defaultState: { sort: null },
              });
              pendingInvoicesSortRestoreRef.current = false;
            } catch (e) {
              if (attempt < 5) restoreSort(attempt + 1);
              else pendingInvoicesSortRestoreRef.current = false;
            }
          } else if (attempt < 5) {
            restoreSort(attempt + 1);
          } else {
            pendingInvoicesSortRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreSort();
    }
  }, [loading, data.length]);

  // Effect to restore credits sort AFTER data has loaded
  useEffect(() => {
    if (
      pendingCreditsSortRestoreRef.current &&
      !loading &&
      creditNotesData.length > 0 &&
      savedCreditsSortRef.current &&
      savedCreditsSortRef.current.length > 0
    ) {
      const restoreSort = (attempt = 1) => {
        setTimeout(() => {
          if (creditsGridApiRef.current) {
            try {
              creditsGridApiRef.current.applyColumnState({
                state: savedCreditsSortRef.current,
                defaultState: { sort: null },
              });
              pendingCreditsSortRestoreRef.current = false;
            } catch (e) {
              if (attempt < 5) restoreSort(attempt + 1);
              else pendingCreditsSortRestoreRef.current = false;
            }
          } else if (attempt < 5) {
            restoreSort(attempt + 1);
          } else {
            pendingCreditsSortRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreSort();
    }
  }, [loading, creditNotesData.length]);

  // Date filter for dashboard statistics
  const [dashboardDateFilter, setDashboardDateFilter] = useState("this_month");
  const [dashboardCustomRange, setDashboardCustomRange] = useState(null);

  // Get invoice status function
  const getInvoiceStatus = useCallback((invoice) => {
    const now = dayjs();
    const dueDate = invoice.due_date ? dayjs(invoice.due_date) : null;
    const totalAmount = parseFloat(invoice.total_amount || 0);
    const paidAmount = parseFloat(invoice.paid_amount || 0);

    if (paidAmount >= totalAmount) {
      return "paid";
    } else if (dueDate && now.isAfter(dueDate)) {
      return "overdue";
    } else {
      return "pending";
    }
  }, []);

  // Process invoice response - same as working Invoices component
  const processInvoiceResponse = useCallback(
    (response) => {
      const invoiceData = response?.results || [];

      const formattedData = invoiceData.map((invoice) => {
        // Calculate payment percentage
        const total = parseFloat(invoice.total_amount || 0);
        const allocatedAmount = parseFloat(
          invoice.allocated_amount || invoice.paid_amount || 0
        );
        const percentPaid = total > 0 ? (allocatedAmount / total) * 100 : 0;

        // Determine allocation status
        let allocationStatus;
        if (percentPaid >= 99.99) {
          allocationStatus = {
            type: "fully_paid",
            label: "Fully Paid",
            color: "success",
          };
        } else if (percentPaid > 0) {
          allocationStatus = {
            type: "partially_paid",
            label: "Partially Paid",
            color: "warning",
          };
        } else {
          allocationStatus = {
            type: "unpaid",
            label: "Unpaid",
            color: "default",
          };
        }

        return {
          id: invoice.id,
          number: invoice.number || `INV-${invoice.id}`,
          partner: invoice.partner?.name || "Unknown",
          invoice_type: "invoice",
          issueDate: invoice.issue_date,
          dueDate: invoice.due_date,
          paidDate: invoice.paid_date, // Add paidDate field
          subtotal: invoice.subtotal || 0,
          discount_amount: invoice.discount_amount || 0,
          tax_rate: invoice.tax_rate || 0,
          total: parseFloat(invoice.total_amount || 0).toFixed(decimalPrecision),
          paid_amount: parseFloat(invoice.paid_amount || 0),
          allocated_amount: allocatedAmount, // Store the calculated allocated amount
          status: invoice.status || "draft",
          approval_status: invoice.approval_status || "draft", // Include approval_status
          senior_approver: invoice.senior_approver, // Include senior approver info
          senior_approval_date: invoice.senior_approval_date,
          management_approver: invoice.management_approver, // Include management approver info
          management_approval_date: invoice.management_approval_date,
          rejection_reason: invoice.rejection_reason,
          rejected_by: invoice.rejected_by,
          allocations: invoice.allocations || [], // Use allocations (includes payment, credit, cash, overpayment)
          items_count:
            invoice.items_count ||
            (Array.isArray(invoice.items) ? invoice.items.length : 0),
          currency: invoice.currency || "$",
          percentPaid,
          allocationStatus,
          last_modified_by: invoice.last_modified_by || "Unknown",
          description: invoice.notes || "",
          originalData: invoice,
        };
      });

      // Sort invoices by issue date in descending order (most recent first)
      const sortedInvoices = formattedData.sort((a, b) => {
        const dateA = dayjs(a.issueDate);
        const dateB = dayjs(b.issueDate);
        return dateB.diff(dateA); // Descending order
      });

      // Force a new array reference to trigger AG Grid re-render
      setData([...sortedInvoices]);
    },
    [getInvoiceStatus]
  );

  // Fetch invoices data - SIMPLE STABLE LOADING (no progressive updates that cause overhead)
  const fetchInvoicesData = useCallback(
    async (force = false) => {
      if (!selectedBrands || selectedBrands.length === 0) {
        return;
      }

      const cacheKey = CacheKeys.invoices(brandsKey);

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedInvoices = getCachedData(cacheKey);
        if (cachedInvoices) {
          setData(cachedInvoices);
          setLoading(false);
          fetchingInvoicesRef.current = false;
          lastFetchedInvoicesBrandsKey.current = brandsKey;
          return;
        }
      }

      // CRITICAL: Check if we already have data loaded for this EXACT brand combination
      if (
        !force &&
        data.length > 0 &&
        lastFetchedInvoicesBrandsKey.current === brandsKey
      ) {
        setLoading(false);
        fetchingInvoicesRef.current = false;
        return;
      }

      // Prevent duplicate simultaneous requests
      if (fetchingInvoicesRef.current && !force) {
        return;
      }

      fetchingInvoicesRef.current = true;
      setLoading(true);

      const brandIds = selectedBrands.map((b) => b.id).join(",");

      try {
        // Fetch ALL data at once - simpler and faster than progressive loading
        const response = await getAllInvoicesParallel({
          include_allocations: true,
          brand: brandIds,
          invoice_type: "invoice",
        });

        const invoiceData = response?.results || [];

        // Format the data BEFORE caching (same as AccountsPayable)
        const formattedData = invoiceData.map((invoice) => {
          const total = parseFloat(invoice.total_amount || 0);
          const allocatedAmount = parseFloat(
            invoice.allocated_amount || invoice.paid_amount || 0
          );
          const percentPaid = total > 0 ? (allocatedAmount / total) * 100 : 0;

          let allocationStatus;
          if (percentPaid >= 99.99) {
            allocationStatus = {
              type: "fully_paid",
              label: "Fully Paid",
              color: "success",
            };
          } else if (percentPaid > 0) {
            allocationStatus = {
              type: "partially_paid",
              label: "Partially Paid",
              color: "warning",
            };
          } else {
            allocationStatus = {
              type: "unpaid",
              label: "Unpaid",
              color: "default",
            };
          }

          return {
            id: invoice.id,
            number: invoice.number || `INV-${invoice.id}`,
            partner:
              invoice.partner?.name ||
              (typeof invoice.partner === "string"
                ? invoice.partner
                : "Unknown"),
            clientName: invoice.client_name || "N/A",
            brandName: invoice.brand?.name || invoice.brand_name || "N/A",
            brandId: invoice.brand?.id || invoice.brand_id || null,
            invoice_type: "invoice",
            issueDate: invoice.issue_date,
            dueDate: invoice.due_date,
            paidDate: invoice.paid_date,
            subtotal: invoice.subtotal || 0,
            discount_amount: invoice.discount_amount || 0,
            tax_rate: invoice.tax_rate || 0,
            total: parseFloat(invoice.total_amount || 0).toFixed(decimalPrecision),
            paid_amount: parseFloat(invoice.paid_amount || 0),
            allocated_amount: allocatedAmount,
            status: invoice.status || "draft",
            approval_status: invoice.approval_status || "draft",
            senior_approver: invoice.senior_approver,
            senior_approval_date: invoice.senior_approval_date,
            management_approver: invoice.management_approver,
            management_approval_date: invoice.management_approval_date,
            rejection_reason: invoice.rejection_reason,
            rejected_by: invoice.rejected_by,
            allocations: invoice.allocations || [],
            items_count:
              invoice.items_count ||
              (Array.isArray(invoice.items) ? invoice.items.length : 0),
            currency: invoice.currency || "$",
            percentPaid,
            allocationStatus,
            last_modified_by: invoice.last_modified_by || "Unknown",
            description: invoice.notes || "",
            originalData: invoice,
          };
        });

        // Sort invoices by issue date in descending order (most recent first)
        const sortedInvoices = formattedData.sort((a, b) => {
          const dateA = dayjs(a.issueDate);
          const dateB = dayjs(b.issueDate);
          return dateB.diff(dateA);
        });

        // Set state with formatted data
        setData([...sortedInvoices]);

        // Cache FORMATTED data in GLOBAL cache (5 minute TTL)
        setCachedData(cacheKey, sortedInvoices, CacheTTL.MEDIUM);
        lastFetchedInvoicesBrandsKey.current = brandsKey;
      } catch {
        message.error("Failed to load invoices");
        setData([]);
      } finally {
        setLoading(false);
        fetchingInvoicesRef.current = false;
      }
    },
    [selectedBrands, brandsKey]
  );

  // Format credit data helper
  const formatCredit = useCallback((credit) => {
    const total = parseFloat(credit.total_amount || 0);
    const allocatedAmount = parseFloat(credit.allocated_amount || 0);
    const percentAllocated = total > 0 ? (allocatedAmount / total) * 100 : 0;

    let allocationStatus;
    if (percentAllocated >= 99.99) {
      allocationStatus = {
        type: "fully_allocated",
        label: "Fully Allocated",
        color: "success",
      };
    } else if (percentAllocated > 0) {
      allocationStatus = {
        type: "partially_allocated",
        label: "Partially Allocated",
        color: "warning",
      };
    } else {
      allocationStatus = {
        type: "unallocated",
        label: "Unallocated",
        color: "default",
      };
    }

    const remainingAmount = total - allocatedAmount;
    let creditStatus;
    if (remainingAmount <= 0.01) {
      creditStatus = "Fully Allocated";
    } else if (remainingAmount < total) {
      creditStatus = "Partially Allocated";
    } else {
      creditStatus = "Draft";
    }

    return {
      id: credit.id,
      number: credit.number || `CR-${credit.id}`,
      partner:
        credit.partner?.name ||
        credit.partner?.partner_name ||
        (typeof credit.partner === "string" ? credit.partner : "Unknown"),
      clientName: credit.client_name || "N/A",
      brandName: credit.brand?.name || credit.brand_name || "N/A",
      brandId: credit.brand?.id || credit.brand_id || null,
      partnerType: credit.credit_type === "invoice" ? "customer" : "vendor",
      credit_type: credit.credit_type || "invoice",
      issueDate: credit.issue_date,
      total: parseFloat(credit.total_amount || 0),
      allocated_amount: parseFloat(credit.allocated_amount || 0),
      remaining_amount: remainingAmount,
      originalData: credit,
      allocations:
        credit.credit_allocations?.map((allocation) => ({
          id: allocation.id,
          amount: allocation.amount,
          allocation_date: allocation.allocation_date,
          invoice_number: allocation.invoice_number || "",
        })) || [],
      allocationStatus: allocationStatus,
      percentAllocated: percentAllocated,
      currency: credit.currency || "$",
      tax_rate: credit.tax_rate,
      subtotal: credit.subtotal,
      discount_amount: credit.discount_amount,
      status: creditStatus,
      approval_status: credit.approval_status || "draft",
      senior_approver: credit.senior_approver,
      senior_approval_date: credit.senior_approval_date,
      management_approver: credit.management_approver,
      management_approval_date: credit.management_approval_date,
      rejection_reason: credit.rejection_reason,
      rejected_by: credit.rejected_by,
      last_modified_by: credit.last_modified_by || "Unknown",
    };
  }, []);

  // Fetch credit notes data from API - SIMPLE STABLE LOADING (no progressive updates that cause overhead)
  const fetchCreditNotesData = useCallback(
    async (force = false) => {
      if (!selectedBrands || selectedBrands.length === 0) {
        return;
      }

      const cacheKey = CacheKeys.invoiceCredits(brandsKey);

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedCredits = getCachedData(cacheKey);
        if (cachedCredits) {
          setCreditNotesData(cachedCredits);
          setLoading(false);
          fetchingCreditsRef.current = false;
          lastFetchedCreditsBrandsKey.current = brandsKey;
          return;
        }
      }

      // CRITICAL: Check if we already have data loaded for this EXACT brand combination
      if (
        !force &&
        creditNotesData.length > 0 &&
        lastFetchedCreditsBrandsKey.current === brandsKey
      ) {
        setLoading(false);
        fetchingCreditsRef.current = false;
        return;
      }

      // Prevent duplicate simultaneous requests
      if (fetchingCreditsRef.current && !force) {
        return;
      }

      fetchingCreditsRef.current = true;
      setLoading(true);

      const brandIds = selectedBrands.map((b) => b.id).join(",");

      try {
        // Fetch ALL data at once - simpler and faster than progressive loading
        const response = await getAllCreditsParallel({
          include_allocations: true,
          brand: brandIds,
          credit_type: "invoice",
        });

        let dataToProcess =
          response?.results || response?.data || response || [];
        const allCredits = Array.isArray(dataToProcess) ? dataToProcess : [];
        const filteredData = allCredits.filter(
          (credit) => credit.credit_type === "invoice"
        );
        const formattedData = filteredData.map((credit) =>
          formatCredit(credit)
        );

        // Sort by issue date descending
        const sortedCredits = formattedData.sort((a, b) => {
          const dateA = dayjs(a.issueDate);
          const dateB = dayjs(b.issueDate);
          return dateB.diff(dateA);
        });

        setCreditNotesData([...sortedCredits]);

        // Cache FORMATTED data in GLOBAL cache (5 minute TTL)
        setCachedData(cacheKey, sortedCredits, CacheTTL.MEDIUM);
        lastFetchedCreditsBrandsKey.current = brandsKey;
      } catch {
        message.error("Failed to load credit notes");
        setCreditNotesData([]);
      } finally {
        setLoading(false);
        fetchingCreditsRef.current = false;
      }
    },
    [selectedBrands, brandsKey, formatCredit]
  );

  // Fetch history data - fetch ALL pages
  const fetchHistoryData = useCallback(
    async (force = false) => {
      if (!firstBrand) return;

      const cacheKey = CacheKeys.invoiceHistory(brandsKey);

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedHistory = getCachedData(cacheKey);
        if (cachedHistory) {
          setHistoryData(cachedHistory);
          return;
        }
      }

      try {
        const apiFilters = {
          include_allocations: true,
          brand: firstBrand.id,
          invoice_type: "invoice",
          start_date: dayjs().subtract(3, "years").format("YYYY-MM-DD"),
          end_date: dayjs().format("YYYY-MM-DD"),
        };

        // Use getAllInvoicesParallel which now fetches all pages automatically
        getAllInvoicesParallel(apiFilters)
          .then((response) => {
            const invoiceData = response?.results || [];

            const formattedData = invoiceData.map((invoice) => {
              const total = parseFloat(invoice.total_amount || 0);
              const allocatedAmount = parseFloat(
                invoice.allocated_amount || invoice.paid_amount || 0
              );
              const percentPaid =
                total > 0 ? (allocatedAmount / total) * 100 : 0;

              let allocationStatus;
              if (percentPaid >= 99.99) {
                allocationStatus = {
                  type: "fully_paid",
                  label: "Fully Paid",
                  color: "success",
                };
              } else if (percentPaid > 0) {
                allocationStatus = {
                  type: "partially_paid",
                  label: "Partially Paid",
                  color: "warning",
                };
              } else {
                allocationStatus = {
                  type: "unpaid",
                  label: "Unpaid",
                  color: "default",
                };
              }

              return {
                id: invoice.id,
                number: invoice.number || `INV-${invoice.id}`,
                partner: invoice.partner?.name || "Unknown",
                invoice_type: "invoice",
                issueDate: invoice.issue_date,
                dueDate: invoice.due_date,
                paidDate: invoice.paid_date,
                subtotal: invoice.subtotal || 0,
                discount_amount: invoice.discount_amount || 0,
                tax_rate: invoice.tax_rate || 0,
                total: parseFloat(invoice.total_amount || 0).toFixed(decimalPrecision),
                paid_amount: parseFloat(invoice.paid_amount || 0),
                status: invoice.status || "draft",
                approval_status: invoice.approval_status || "draft", // Include approval_status
                senior_approver: invoice.senior_approver, // Include senior approver info
                senior_approval_date: invoice.senior_approval_date,
                management_approver: invoice.management_approver, // Include management approver info
                management_approval_date: invoice.management_approval_date,
                rejection_reason: invoice.rejection_reason,
                rejected_by: invoice.rejected_by,
                allocations: invoice.allocations || [], // Use allocations (includes payment, credit, cash, overpayment)
                items_count:
                  invoice.items_count ||
                  (Array.isArray(invoice.items) ? invoice.items.length : 0),
                currency: invoice.currency || "$",
                percentPaid,
                allocationStatus,
                last_modified_by: invoice.last_modified_by || "Unknown",
                description: invoice.notes || "",
                createdDate: invoice.created_at
                  ? dayjs(invoice.created_at).format("MMM DD, YYYY")
                  : "N/A",
                originalData: invoice,
              };
            });

            setHistoryData(formattedData);

            // Cache in GLOBAL cache (5 minute TTL)
            setCachedData(cacheKey, formattedData, CacheTTL.MEDIUM);
          })
          .catch(() => {
            setHistoryData([]);
          });
      } catch {
        setHistoryData([]);
      }
    },
    [firstBrand, brandsKey]
  );

  // Helper to check if cache data is corrupted (has "[object Object]" in partner fields)
  const isCacheCorrupted = useCallback((cachedData) => {
    if (!Array.isArray(cachedData) || cachedData.length === 0) return false;
    // Check first few items for corruption
    return cachedData.slice(0, 5).some((item) => {
      const partner = item.partner || item.customer;
      return typeof partner === "string" && partner.includes("[object");
    });
  }, []);

  // Effect for fetching data - depend on selectedBrands to trigger on any brand change
  useEffect(() => {
    if (!brandsKey) {
      setData([]);
      setCreditNotesData([]);
      setHistoryData([]);
      return;
    }

    // CRITICAL: Check GLOBAL cache immediately - if we have cached data, don't refetch
    const invoicesCacheKey = CacheKeys.invoices(brandsKey);
    const creditsCacheKey = CacheKeys.invoiceCredits(brandsKey);
    const historyCacheKey = CacheKeys.invoiceHistory(brandsKey);
    const cachedInvoices = getCachedData(invoicesCacheKey);
    const cachedCredits = getCachedData(creditsCacheKey);
    const cachedHistory = getCachedData(historyCacheKey);

    // Check for corrupted cache and invalidate if needed
    const invoicesCorrupted = isCacheCorrupted(cachedInvoices);
    const creditsCorrupted = isCacheCorrupted(cachedCredits);
    const historyCorrupted = isCacheCorrupted(cachedHistory);

    if (invoicesCorrupted || creditsCorrupted || historyCorrupted) {
      // Invalidate corrupted cache and force refetch
      invalidateCacheByPrefix("invoices_");
      invalidateCacheByPrefix("invoice_credits_");
      invalidateCacheByPrefix("invoice_history_");
      if (fetchInvoicesDataRef.current) fetchInvoicesDataRef.current(true);
      if (fetchCreditNotesDataRef.current)
        fetchCreditNotesDataRef.current(true);
      if (fetchHistoryDataRef.current) fetchHistoryDataRef.current(true);
      return;
    }

    // RESTORE FROM CACHE IMMEDIATELY - no refetch needed!
    if (cachedInvoices) {
      setData(cachedInvoices);
      lastFetchedInvoicesBrandsKey.current = brandsKey;
    }

    if (cachedCredits) {
      setCreditNotesData(cachedCredits);
      lastFetchedCreditsBrandsKey.current = brandsKey;
    }

    if (cachedHistory) {
      setHistoryData(cachedHistory);
    }

    // Only fetch if NOT already cached
    if (!cachedInvoices && fetchInvoicesDataRef.current)
      fetchInvoicesDataRef.current();
    if (!cachedCredits && fetchCreditNotesDataRef.current)
      fetchCreditNotesDataRef.current();
    if (!cachedHistory && fetchHistoryDataRef.current)
      fetchHistoryDataRef.current();
  }, [brandsKey, isCacheCorrupted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get date range based on filter
  const getDateRangeFromFilter = useCallback(
    (filter) => {
      const today = dayjs();
      let start, end;

      switch (filter) {
        case "this_month":
          start = today.startOf("month");
          end = today.endOf("month");
          break;
        case "last_month":
          start = today.subtract(1, "month").startOf("month");
          end = today.subtract(1, "month").endOf("month");
          break;
        case "this_year":
          start = today.startOf("year");
          end = today.endOf("month"); // YTD = year start to today
          break;
        case "custom":
          if (dashboardCustomRange && dashboardCustomRange.length === 2) {
            start = dashboardCustomRange[0];
            end = dashboardCustomRange[1];
          } else {
            start = today.startOf("month");
            end = today.endOf("month");
          }
          break;
        default:
          start = today.startOf("month");
          end = today.endOf("month");
      }

      return { start, end };
    },
    [dashboardCustomRange]
  );

  // Calculate statistics from actual data - NOW FETCHED FROM BACKEND
  const fetchStatistics = useCallback(
    async (force = false) => {
      if (!brandsKey) return;

      const { start, end } = getDateRangeFromFilter(dashboardDateFilter);
      const cacheKey = `ar-stats-${brandsKey}-${start.format(
        "YYYY-MM-DD"
      )}-${end.format("YYYY-MM-DD")}`;

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedStats = getCachedData(cacheKey);
        if (cachedStats) {
          setStatistics(cachedStats);
          return;
        }
      }

      try {
        setLoading(true);
        const stats = await getAccountsReceivableStatistics({
          brand_ids: brandsKey.split(","),
          start_date: start.format("YYYY-MM-DD"),
          end_date: end.format("YYYY-MM-DD"),
        });
        setStatistics(stats);

        // Cache in GLOBAL cache (5 minute TTL)
        setCachedData(cacheKey, stats, CacheTTL.MEDIUM);
      } catch {
        message.error("Could not load dashboard statistics.");
      } finally {
        setLoading(false);
      }
    },
    [brandsKey, dashboardDateFilter, getDateRangeFromFilter]
  );

  // Keep refs updated
  useEffect(() => {
    fetchInvoicesDataRef.current = fetchInvoicesData;
    fetchCreditNotesDataRef.current = fetchCreditNotesData;
    fetchHistoryDataRef.current = fetchHistoryData;
    fetchStatisticsRef.current = fetchStatistics;
  }, [
    fetchInvoicesData,
    fetchCreditNotesData,
    fetchHistoryData,
    fetchStatistics,
  ]);

  // Fetch / recalculate statistics when filters or selected brands change.
  // This is the single source of truth for loading dashboard stats to avoid
  // duplicate requests on mount or when the user role changes.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (fetchStatisticsRef.current) {
        fetchStatisticsRef.current();
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [brandsKey, dashboardDateFilter, dashboardCustomRange]);

  // Debounced refresh to prevent multiple rapid refreshes
  // CRITICAL: Preserves filter state across refreshes
  const handleRefresh = React.useCallback(() => {
    // Clear any pending refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // CRITICAL: Save current filter and sort state BEFORE refreshing
    if (invoicesGridApiRef.current) {
      try {
        const currentFilters = invoicesGridApiRef.current.getFilterModel();
        if (currentFilters && Object.keys(currentFilters).length > 0) {
          savedInvoicesFilterRef.current = currentFilters;
          pendingInvoicesFilterRestoreRef.current = true;
        }
        // Save sort state
        const columnState = invoicesGridApiRef.current.getColumnState();
        const sortState = columnState
          .filter((col) => col.sort !== null)
          .map((col) => ({
            colId: col.colId,
            sort: col.sort,
            sortIndex: col.sortIndex,
          }));
        if (sortState.length > 0) {
          savedInvoicesSortRef.current = sortState;
          pendingInvoicesSortRestoreRef.current = true;
        }
      } catch (e) {
        // Grid might not be ready, ignore
      }
    }
    if (creditsGridApiRef.current) {
      try {
        const currentFilters = creditsGridApiRef.current.getFilterModel();
        if (currentFilters && Object.keys(currentFilters).length > 0) {
          savedCreditsFilterRef.current = currentFilters;
          pendingCreditsFilterRestoreRef.current = true;
        }
        // Save sort state
        const columnState = creditsGridApiRef.current.getColumnState();
        const sortState = columnState
          .filter((col) => col.sort !== null)
          .map((col) => ({
            colId: col.colId,
            sort: col.sort,
            sortIndex: col.sortIndex,
          }));
        if (sortState.length > 0) {
          savedCreditsSortRef.current = sortState;
          pendingCreditsSortRestoreRef.current = true;
        }
      } catch (e) {
        // Grid might not be ready, ignore
      }
    }

    // Invalidate global cache for invoices
    invalidateCacheByPrefix("invoices_");
    invalidateCacheByPrefix("invoice_credits_");
    invalidateCacheByPrefix("invoice_history_");
    invalidateCacheByPrefix("ar-stats-");

    // Debounce the refresh by 300ms to batch multiple calls
    refreshTimeoutRef.current = setTimeout(() => {
      if (fetchInvoicesDataRef.current) fetchInvoicesDataRef.current(true);
      if (fetchCreditNotesDataRef.current)
        fetchCreditNotesDataRef.current(true);
      if (fetchHistoryDataRef.current) fetchHistoryDataRef.current(true);
      if (fetchStatisticsRef.current) fetchStatisticsRef.current(true);
    }, 300);
  }, [
    fetchInvoicesData,
    fetchCreditNotesData,
    fetchHistoryData,
    fetchStatistics,
  ]);

  // Handle delete invoice - with allocation check
  const handleDeleteInvoice = useCallback(
    (id, invoiceData) => {
      // Show delete confirmation
      Modal.confirm({
        title: "Delete Invoice",
        content: `Are you sure you want to delete Invoice #${
          invoiceData?.number || id
        }?`,
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await deleteInvoice(id);
            message.success("Invoice deleted successfully");
            // Clear API cache before refresh to ensure fresh data
            clearAccountingCache();
            clearOverpaymentCache();
            // Use handleRefresh to preserve filter state
            handleRefresh();
          } catch (error) {
            console.error("Error deleting invoice:", error);
            const backendMsg =
              error?.response?.data?.detail ||
              error?.response?.data?.message ||
              (typeof error?.response?.data === "string"
                ? error.response.data
                : null);

            // Check if the error is about allocations
            if (backendMsg && backendMsg.toLowerCase().includes("allocation")) {
              Modal.error({
                title: "Cannot Delete Invoice - Has Allocations",
                content: (
                  <div>
                    <p>{backendMsg}</p>
                    <Button
                      type="primary"
                      style={{ marginTop: 16 }}
                      onClick={() => {
                        Modal.destroyAll();
                        navigate(`/accounting/transactions-allocations`, {
                          state: {
                            invoiceId: id,
                            invoiceNumber: invoiceData?.number,
                          },
                        });
                      }}
                    >
                      Go to Transactions & Allocations
                    </Button>
                  </div>
                ),
              });
              return;
            }

            // Other errors
            Modal.error({
              title: "Cannot Delete Invoice",
              content:
                backendMsg || "An error occurred while deleting the invoice.",
            });
          }
        },
      });
    },
    [handleRefresh]
  );

  // Handle edit invoice - same as Invoices.jsx
  const handleEditInvoice = useCallback((id) => {
    setEditingInvoiceId(id);
    setCreateInvoiceModalVisible(true);
  }, []);

  // Handle view invoice
  const handleViewInvoice = useCallback((id) => {
    setViewingInvoiceId(id);
    setViewingCreditId(null); // Ensure we're not viewing a credit
    setViewInvoiceModalVisible(true);
  }, []);

  // Handle view credit note
  const handleViewCredit = useCallback((id) => {
    setViewingCreditId(id);
    setViewingInvoiceId(null); // Ensure we're not viewing an invoice
    setViewInvoiceModalVisible(true);
  }, []);

  // Handle view payments modal
  const handleViewPayments = useCallback(async (invoiceData) => {
    // If it's a credit and allocations are missing, fetch them
    if (
      invoiceData.credit_type &&
      (!invoiceData.allocations || invoiceData.allocations.length === 0)
    ) {
      try {
        const creditData = await getCreditById(invoiceData.id, true);
        // Update invoiceData with fetched allocations
        invoiceData = {
          ...invoiceData,
          allocations:
            creditData.credit_allocations || creditData.allocations || [],
          allocated_amount: creditData.allocated_amount || 0,
        };
      } catch (error) {
        console.error("Error fetching credit allocations:", error);
        message.error("Failed to load credit allocations");
      }
    }
    setSelectedInvoiceForPayments(invoiceData);
    setPaymentsModalVisible(true);
  }, []);

  // Handle export PDF for invoices
  const handleExportPDF = useCallback(async (id) => {
    try {
      const response = await exportInvoicePDF(id);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoice_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      message.error("Failed to export PDF");
    }
  }, []);

  // Handle export PDF for credit notes
  const handleExportCreditPDF = useCallback(async (id) => {
    try {
      const response = await exportCreditPDF(id);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Credit_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error exporting credit PDF:", error);
      message.error("Failed to export PDF");
    }
  }, []);

  // Handle create invoice success
  const handleCreateInvoiceSuccess = useCallback(() => {
    setCreateInvoiceModalVisible(false);
    setEditingInvoiceId(null);
    // Use handleRefresh to preserve filter state
    handleRefresh();
  }, [handleRefresh]);

  // Handle delete credit note - with allocation check
  const handleDeleteCredit = (id, creditData) => {
    // Check if the credit has any allocations
    const hasAllocations =
      creditData?.allocations && creditData.allocations.length > 0;
    const allocatedAmount = parseFloat(creditData?.allocated_amount || 0);

    if (hasAllocations || allocatedAmount > 0) {
      // Show modal with link to allocations
      Modal.warning({
        title: "Cannot Delete Credit Note",
        content: (
          <div>
            <p>This credit note has allocations and cannot be deleted.</p>
            <p>
              <strong>Allocated Amount:</strong> $
              {allocatedAmount.toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              })}
            </p>
            <p>Please unallocate all credits before deleting.</p>
          </div>
        ),
        okText: "Go to Allocations",
        onOk: () => {
          // Navigate to Transactions & Allocations tab
          window.location.href = "/accounting/transactions-allocations";
        },
      });
      return;
    }

    // Proceed with delete if no allocations
    Modal.confirm({
      title: "Are you sure you want to delete this credit note?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteCredit(id);
          message.success("Credit note deleted successfully");
          // Clear API cache before refresh to ensure fresh data
          clearAccountingCache();
          clearOverpaymentCache();
          // Use handleRefresh to preserve filter state
          handleRefresh();
        } catch (error) {
          console.error("Error deleting credit note:", error);
          const backendMsg =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            (typeof error?.response?.data === "string"
              ? error.response.data
              : null);
          if (backendMsg) {
            Modal.warning({
              title: "Cannot delete credit note",
              content: backendMsg,
            });
          } else {
            Modal.warning({
              title: "Cannot delete credit note",
              content:
                "This credit note has allocations. Please unallocate first.",
            });
          }
        }
      },
    });
  };

  const handleBulkDeleteInvoices = useCallback(() => {
    if (selectedInvoiceIds.length === 0) return;
    const selectedItems = selectedInvoiceIds
      .map((id) => data.find((item) => item.id === id))
      .filter(Boolean);
    const hasAllocated = selectedItems.some(
      (item) =>
        parseFloat(item.allocated_amount || 0) > 0 ||
        (item.allocations && item.allocations.length > 0)
    );
    if (hasAllocated) {
      Modal.warning({
        title: "Cannot Delete - Some Invoices Have Allocations",
        content:
          "Some selected invoices have payments allocated. Please unallocate them first, or deselect them and try again.",
      });
      return;
    }
    Modal.confirm({
      title: `Delete ${selectedInvoiceIds.length} Invoice(s)?`,
      content: `This will permanently delete ${selectedInvoiceIds.length} selected invoice(s). This action cannot be undone.`,
      okText: "Delete All",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;
        for (const id of selectedInvoiceIds) {
          try {
            await deleteInvoice(id);
            successCount++;
          } catch {
            failCount++;
          }
        }
        if (successCount > 0) {
          message.success(`Deleted ${successCount} invoice(s)`);
          clearAccountingCache();
          clearOverpaymentCache();
          setSelectedInvoiceIds([]);
          handleRefresh();
        }
        if (failCount > 0) {
          message.error(
            `Failed to delete ${failCount} invoice(s) — they may have allocations.`
          );
        }
      },
    });
  }, [selectedInvoiceIds, data, handleRefresh]);

  const handleBulkDeleteCredits = useCallback(() => {
    if (selectedCreditIds.length === 0) return;
    const selectedItems = selectedCreditIds
      .map((id) => creditNotesData.find((item) => item.id === id))
      .filter(Boolean);
    const hasAllocated = selectedItems.some(
      (item) =>
        parseFloat(item.allocated_amount || 0) > 0 ||
        (item.allocations && item.allocations.length > 0)
    );
    if (hasAllocated) {
      Modal.warning({
        title: "Cannot Delete - Some Credits Have Allocations",
        content:
          "Some selected credits have allocations. Please unallocate them first, or deselect them and try again.",
      });
      return;
    }
    Modal.confirm({
      title: `Delete ${selectedCreditIds.length} Credit(s)?`,
      content: `This will permanently delete ${selectedCreditIds.length} selected credit note(s). This action cannot be undone.`,
      okText: "Delete All",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;
        for (const id of selectedCreditIds) {
          try {
            await deleteCredit(id);
            successCount++;
          } catch {
            failCount++;
          }
        }
        if (successCount > 0) {
          message.success(`Deleted ${successCount} credit note(s)`);
          clearAccountingCache();
          clearOverpaymentCache();
          setSelectedCreditIds([]);
          handleRefresh();
        }
        if (failCount > 0) {
          message.error(
            `Failed to delete ${failCount} credit(s) — they may have allocations.`
          );
        }
      },
    });
  }, [selectedCreditIds, creditNotesData, handleRefresh]);

  // Handle edit credit note
  const handleEditCredit = (id) => {
    setEditingCreditId(id);
    setCreateCreditModalVisible(true);
  };

  // Handle create credit success
  const handleCreateCreditSuccess = () => {
    setCreateCreditModalVisible(false);
    setEditingCreditId(null);
    // Use handleRefresh to preserve filter state
    handleRefresh();
  };

  // Handle approve invoice
  const handleApproveInvoice = async (invoiceId) => {
    try {
      await approveInvoice(invoiceId);
      message.success("Invoice approved successfully");
      // Use handleRefresh to preserve filter state
      handleRefresh();
    } catch (error) {
      console.error("Error approving invoice:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to approve invoice";
      message.error(errorMsg);
    }
  };

  // Handle reject invoice
  const handleRejectInvoice = async (invoiceId) => {
    Modal.confirm({
      title: "Reject Invoice",
      content: (
        <div>
          <p>Please provide a reason for rejection (optional):</p>
          <Input.TextArea
            id="rejection-reason-invoice-input"
            rows={4}
            placeholder="Enter rejection reason..."
            autoFocus
          />
        </div>
      ),
      okText: "Reject",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const reasonInput = document.getElementById(
          "rejection-reason-invoice-input"
        );
        const rejectionReason = reasonInput?.value || "";
        try {
          await rejectInvoice(invoiceId, rejectionReason);
          message.success("Invoice rejected and reset to draft");
          // Use handleRefresh to preserve filter state
          handleRefresh();
        } catch (error) {
          console.error("Error rejecting invoice:", error);
          const errorMsg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to reject invoice";
          message.error(errorMsg);
        }
      },
    });
  };

  // Handle approve credit
  const handleApproveCredit = async (creditId) => {
    try {
      await approveCredit(creditId);
      message.success("Credit approved successfully");
      // Use handleRefresh to preserve filter state
      handleRefresh();
    } catch (error) {
      console.error("Error approving credit:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to approve credit";
      message.error(errorMsg);
    }
  };

  // Handle reject credit
  const handleRejectCredit = async (creditId) => {
    Modal.confirm({
      title: "Reject Credit",
      content: (
        <div>
          <p>Please provide a reason for rejection (optional):</p>
          <Input.TextArea
            id="rejection-reason-credit-ar-input"
            rows={4}
            placeholder="Enter rejection reason..."
            autoFocus
          />
        </div>
      ),
      okText: "Reject",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const reasonInput = document.getElementById(
          "rejection-reason-credit-ar-input"
        );
        const rejectionReason = reasonInput?.value || "";
        try {
          await rejectCredit(creditId, rejectionReason);
          message.success("Credit rejected and reset to draft");
          // Use handleRefresh to preserve filter state
          handleRefresh();
        } catch (error) {
          console.error("Error rejecting credit:", error);
          const errorMsg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to reject credit";
          message.error(errorMsg);
        }
      },
    });
  };

  // Handle open approval modal - fetch single invoice by ID for fast, reliable data
  const handleOpenApprovalModal = useCallback(
    async (item, itemType = "invoice") => {
      setApprovalItemType(itemType);

      try {
        let latestItem;
        if (itemType === "invoice") {
          // Fetch single invoice by ID - much faster than fetching all
          latestItem = await getInvoiceById(item.id);
        } else {
          // Fetch single credit by ID
          latestItem = await getCreditById(item.id, true);
        }

        // Format it the same way as in fetchInvoicesData/fetchCreditNotesData
        if (itemType === "invoice") {
          const total = parseFloat(latestItem.total_amount || 0);
          const allocatedAmount = parseFloat(
            latestItem.allocated_amount || latestItem.paid_amount || 0
          );
          const percentPaid = total > 0 ? (allocatedAmount / total) * 100 : 0;
          let allocationStatus;
          if (percentPaid >= 99.99) {
            allocationStatus = {
              type: "fully_paid",
              label: "Fully Paid",
              color: "success",
            };
          } else if (percentPaid > 0) {
            allocationStatus = {
              type: "partially_paid",
              label: "Partially Paid",
              color: "warning",
            };
          } else {
            allocationStatus = {
              type: "unpaid",
              label: "Unpaid",
              color: "default",
            };
          }
          const formattedItem = {
            id: latestItem.id,
            number: latestItem.number || `INV-${latestItem.id}`,
            partner: latestItem.partner?.name || "Unknown",
            invoice_type: "invoice",
            issueDate: latestItem.issue_date,
            dueDate: latestItem.due_date,
            paidDate: latestItem.paid_date,
            subtotal: latestItem.subtotal || 0,
            discount_amount: latestItem.discount_amount || 0,
            tax_rate: latestItem.tax_rate || 0,
            total: parseFloat(latestItem.total_amount || 0),
            paid_amount: parseFloat(latestItem.paid_amount || 0),
            allocated_amount: allocatedAmount,
            status: latestItem.status || "draft",
            approval_status: latestItem.approval_status || "draft",
            senior_approver: latestItem.senior_approver,
            senior_approval_date: latestItem.senior_approval_date,
            management_approver: latestItem.management_approver,
            management_approval_date: latestItem.management_approval_date,
            rejection_reason: latestItem.rejection_reason,
            rejected_by: latestItem.rejected_by,
            allocations: latestItem.allocations || [],
            items_count:
              latestItem.items_count ||
              (Array.isArray(latestItem.items) ? latestItem.items.length : 0),
            currency: latestItem.currency || "$",
            percentPaid,
            allocationStatus,
            last_modified_by: latestItem.last_modified_by || "Unknown",
            description: latestItem.notes || "",
            originalData: latestItem,
          };
          setSelectedItemForApproval(formattedItem);
        } else {
          const total = parseFloat(latestItem.total_amount || 0);
          const allocatedAmount = parseFloat(latestItem.allocated_amount || 0);
          const percentAllocated =
            total > 0 ? (allocatedAmount / total) * 100 : 0;
          let allocationStatus;
          if (percentAllocated >= 99.99) {
            allocationStatus = {
              type: "fully_allocated",
              label: "Fully Allocated",
              color: "success",
            };
          } else if (percentAllocated > 0) {
            allocationStatus = {
              type: "partially_allocated",
              label: "Partially Allocated",
              color: "warning",
            };
          } else {
            allocationStatus = {
              type: "unallocated",
              label: "Unallocated",
              color: "default",
            };
          }
          const remainingAmount = total - allocatedAmount;
          let creditStatus =
            remainingAmount <= 0.01
              ? "Fully Allocated"
              : remainingAmount < total
              ? "Partially Allocated"
              : "Draft";
          const formattedItem = {
            id: latestItem.id,
            number: latestItem.number || `CR-${latestItem.id}`,
            partner: latestItem.partner?.name || "Unknown",
            credit_type: latestItem.credit_type || "invoice",
            issueDate: latestItem.issue_date,
            subtotal: latestItem.subtotal || 0,
            discount_amount: latestItem.discount_amount || 0,
            tax_rate: latestItem.tax_rate || 0,
            total: parseFloat(latestItem.total_amount || 0),
            allocated_amount: allocatedAmount,
            remaining_amount: remainingAmount,
            allocations: latestItem.credit_allocations || [],
            items_count:
              latestItem.items_count ||
              (Array.isArray(latestItem.items) ? latestItem.items.length : 0),
            currency: latestItem.currency || "$",
            percentAllocated,
            allocationStatus,
            status: creditStatus,
            approval_status: latestItem.approval_status || "draft",
            senior_approver: latestItem.senior_approver,
            senior_approval_date: latestItem.senior_approval_date,
            management_approver: latestItem.management_approver,
            management_approval_date: latestItem.management_approval_date,
            rejection_reason: latestItem.rejection_reason,
            rejected_by: latestItem.rejected_by,
            last_modified_by: latestItem.last_modified_by || "Unknown",
            originalData: latestItem,
          };
          setSelectedItemForApproval(formattedItem);
        }
      } catch (error) {
        console.error("Error fetching latest item for approval modal:", error);
        // Fallback to current item if fetch fails
        setSelectedItemForApproval(item);
      }

      setApprovalModalVisible(true);
    },
    []
  );

  // Handle approval modal success
  const handleApprovalSuccess = useCallback(() => {
    setApprovalModalVisible(false);
    setSelectedItemForApproval(null);
    // Refresh data to get updated approval status
    // Use a small delay to ensure the backend has processed the approval
    clearAccountingCache();
    clearOverpaymentCache();
    // Use handleRefresh to preserve filter state - it handles the delay internally
    setTimeout(() => {
      handleRefresh();
    }, 500); // Small delay to ensure backend has saved
  }, [handleRefresh]);

  // Check if item is eligible for approval based on user role
  // Single-level management approval: management can approve drafts directly
  const isEligibleForApproval = useCallback(
    (item) => {
      const status = (item.approval_status || "draft").toLowerCase();
      if (!userRole) return false;

      const role = userRole.toLowerCase();
      // Only management can approve drafts directly (single-level approval)
      if (role === "management") {
        return status === "draft" || status === "pending_senior_approval" || status === "pending_management_approval";
      }

      return false;
    },
    [userRole]
  );

  // Handle selection change for invoices grid
  const handleInvoiceSelectionChanged = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    setSelectedInvoiceIds(selectedRows.map((row) => row.id));
  }, []);

  // Handle selection change for credits grid
  const handleCreditSelectionChanged = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    setSelectedCreditIds(selectedRows.map((row) => row.id));
  }, []);

  // Toggle invoice selection for approval
  const handleToggleInvoiceSelection = useCallback((invoiceId) => {
    setSelectedInvoiceIds((prev) => {
      const isSelected = prev.includes(invoiceId);
      if (isSelected) {
        return prev.filter((id) => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  }, []);

  // Toggle credit selection for approval
  const handleToggleCreditSelection = useCallback((creditId) => {
    setSelectedCreditIds((prev) => {
      const isSelected = prev.includes(creditId);
      if (isSelected) {
        return prev.filter((id) => id !== creditId);
      } else {
        return [...prev, creditId];
      }
    });
  }, []);

  // Bulk approve handler
  // If items are selected, approve only selected; otherwise approve all eligible
  const handleBulkApprove = useCallback(
    async (itemType = "both", useSelectedOnly = false) => {
      let itemsToApprove = [];

      if (itemType === "invoice" || itemType === "both") {
        const sourceData = data;
        const hasSelection = selectedInvoiceIds.length > 0;
        // If useSelectedOnly and there are selected invoices, use only selected eligible items
        const eligibleInvoices = sourceData.filter((item) => {
          const isEligible = isEligibleForApproval(item);
          if (useSelectedOnly && hasSelection) {
            return isEligible && selectedInvoiceIds.includes(item.id);
          }
          return isEligible;
        });
        itemsToApprove.push(
          ...eligibleInvoices.map((item) => ({
            ...item,
            type: "invoice",
          }))
        );
      }

      if (itemType === "credit" || itemType === "both") {
        const sourceData = creditNotesData;
        const hasSelection = selectedCreditIds.length > 0;
        // If useSelectedOnly and there are selected credits, use only selected eligible items
        const eligibleCredits = sourceData.filter((item) => {
          const isEligible = isEligibleForApproval(item);
          if (useSelectedOnly && hasSelection) {
            return isEligible && selectedCreditIds.includes(item.id);
          }
          return isEligible;
        });
        itemsToApprove.push(
          ...eligibleCredits.map((item) => ({ ...item, type: "credit" }))
        );
      }

      if (itemsToApprove.length === 0) {
        message.warning("No eligible items to approve");
        return;
      }

      // Show confirmation modal
      const isSelectedMode =
        useSelectedOnly &&
        (selectedInvoiceIds.length > 0 || selectedCreditIds.length > 0);
      Modal.confirm({
        title: isSelectedMode ? "Approve Selected Items" : "Bulk Approve Items",
        content: `Are you sure you want to approve ${
          itemsToApprove.length
        } item${itemsToApprove.length > 1 ? "s" : ""}?`,
        okText: isSelectedMode ? "Approve Selected" : "Approve All",
        okType: "primary",
        cancelText: "Cancel",
        onOk: async () => {
          setBulkApproving(true);
          const results = {
            success: [],
            failed: [],
            skipped: [],
          };

          // Clear cache before starting bulk approval
          clearAccountingCache();
          clearOverpaymentCache();

          // Process all items with Promise.allSettled
          const promises = itemsToApprove.map(async (item) => {
            try {
              if (item.type === "invoice") {
                await approveInvoice(item.id);
                results.success.push({
                  id: item.id,
                  number: item.number,
                  type: "Invoice",
                });
              } else {
                await approveCredit(item.id);
                results.success.push({
                  id: item.id,
                  number: item.number,
                  type: "Credit",
                });
              }
            } catch (error) {
              const errorMsg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Unknown error";
              results.failed.push({
                id: item.id,
                number: item.number,
                type: item.type === "invoice" ? "Invoice" : "Credit",
                error: errorMsg,
              });
            }
          });

          await Promise.allSettled(promises);

          // Show summary
          const successCount = results.success.length;
          const failedCount = results.failed.length;

          if (successCount > 0) {
            message.success(
              `Successfully approved ${successCount} item${
                successCount > 1 ? "s" : ""
              }`
            );
          }
          if (failedCount > 0) {
            message.error(
              `Failed to approve ${failedCount} item${
                failedCount > 1 ? "s" : ""
              }`
            );
            console.error("Failed approvals:", results.failed);
          }

          // Clear selections
          setSelectedInvoiceIds([]);
          setSelectedCreditIds([]);

          // Use handleRefresh to preserve filter state
          setTimeout(() => {
            handleRefresh();
          }, 500);

          setBulkApproving(false);
        },
      });
    },
    [
      data,
      creditNotesData,
      selectedInvoiceIds,
      selectedCreditIds,
      isEligibleForApproval,
      handleRefresh,
    ]
  );

  // Get eligible count for display (selected items)
  const eligibleInvoiceCount = useMemo(() => {
    return selectedInvoiceIds
      .map((id) => data.find((item) => item.id === id))
      .filter((item) => item && isEligibleForApproval(item)).length;
  }, [selectedInvoiceIds, data, isEligibleForApproval]);

  const eligibleCreditCount = useMemo(() => {
    return selectedCreditIds
      .map((id) => creditNotesData.find((item) => item.id === id))
      .filter((item) => item && isEligibleForApproval(item)).length;
  }, [selectedCreditIds, creditNotesData, isEligibleForApproval]);

  // Check if there are any items that can be approved (not just selected)
  const hasApprovableInvoices = useMemo(() => {
    return data.some((item) => isEligibleForApproval(item));
  }, [data, isEligibleForApproval]);

  const hasApprovableCredits = useMemo(() => {
    return creditNotesData.some((item) => isEligibleForApproval(item));
  }, [creditNotesData, isEligibleForApproval]);

  const totalApprovableInvoices = useMemo(() => {
    return data.filter((item) => isEligibleForApproval(item)).length;
  }, [data, isEligibleForApproval]);

  const totalApprovableCredits = useMemo(() => {
    return creditNotesData.filter((item) => isEligibleForApproval(item)).length;
  }, [creditNotesData, isEligibleForApproval]);

  // Handle upload invoice — redirects to batch upload
  const showUploadModal = () => {
    setBatchUploadInvoicesVisible(true);
  };

  const handleUploadCancel = () => {
    setUploadModalVisible(false);
    setFileList([]);
    form.resetFields();
  };

  const handleUploadOk = async () => {
    try {
      const values = await form.validateFields();

      if (fileList.length === 0) {
        message.error("Please select a file to upload");
        return;
      }

      const fileObj = fileList[0];
      const fileExtension = fileObj.name.split(".").pop().toLowerCase();

      if (!["xlsx", "xls", "csv"].includes(fileExtension)) {
        message.error(
          "Only Excel (.xlsx, .xls) or CSV (.csv) files are allowed"
        );
        return;
      }

      setUploadLoading(true);

      const billsFileData = {
        brand: firstBrand ? firstBrand.id : null,
        notes: values.notes || "",
        document_type: "invoice",
      };

      const response = await uploadBillsFile(billsFileData, fileObj);

      if (response.processing_results) {
        const {
          partners_created,
          invoices_created,
          message: resultMessage,
          error,
        } = response.processing_results;

        if (error) {
          message.warning(
            <div>
              <p>File was uploaded but there was an error processing it:</p>
              <p>{error}</p>
            </div>,
            8
          );
        } else {
          message.success(
            <div>
              <p>Invoices file uploaded successfully!</p>
              <p>{resultMessage}</p>
              <ul>
                <li>Partners created/found: {partners_created}</li>
                <li>Invoices created: {invoices_created}</li>
              </ul>
            </div>,
            6
          );
        }
      } else {
        message.success("Invoices file uploaded successfully");
      }

      // Use handleRefresh to preserve filter state
      handleRefresh();
      setUploadModalVisible(false);
      setFileList([]);
      form.resetFields();
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorMessage = JSON.stringify(errorData);
        if (
          errorMessage.includes("Duplicate entry") &&
          errorMessage.includes("file_hash")
        ) {
          message.error(
            "This file has already been uploaded. Please upload a different file."
          );
        } else if (typeof errorData === "object") {
          Object.keys(errorData).forEach((key) => {
            message.error(`${key}: ${errorData[key]}`);
          });
        } else {
          message.error("Failed to upload invoices file: " + errorData);
        }
      } else {
        message.error("Failed to upload invoices file: " + error.message);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isAllowedType =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/csv";
      if (!isAllowedType) {
        message.error("You can only upload Excel or CSV files!");
      } else {
        setFileList([file]);
      }
      return false;
    },
    fileList,
  };

  // Export invoices to Excel - directly downloads, respects AG Grid filters
  // Export invoices to Excel - directly downloads, respects AG Grid filters
  // Includes currency conversion when a non-USD currency is selected
  const exportInvoicesToExcel = useCallback(() => {
    try {
      const invoicesToExport = [];
      const gridApi = invoicesGridApiRef.current;

      if (gridApi) {
        const displayedRowCount = gridApi.getDisplayedRowCount();
        for (let i = 0; i < displayedRowCount; i++) {
          const node = gridApi.getDisplayedRowAtIndex(i);
          if (node && node.data) {
            const totalUSD = parseFloat(node.data.total || 0);
            const paidUSD = parseFloat(
              node.data.allocated_amount || node.data.paid_amount || 0
            );

            // Apply currency conversion if active
            const totalDisplay = isConverted
              ? totalUSD * exchangeRate
              : totalUSD;
            const paidDisplay = isConverted ? paidUSD * exchangeRate : paidUSD;

            const exportRow = {
              "Invoice Number": node.data.number || "",
              Client: node.data.clientName || "N/A",
              Brand: node.data.brandName || "N/A",
              Customer: node.data.partner || "",
              "Issue Date": node.data.issueDate
                ? dayjs(node.data.issueDate).format("YYYY-MM-DD")
                : "",
              "Due Date": node.data.dueDate
                ? dayjs(node.data.dueDate).format("YYYY-MM-DD")
                : "",
              [`Total Amount (${displayCurrency})`]:
                totalDisplay.toLocaleString("en-US", {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }),
              [`Paid Amount (${displayCurrency})`]: paidDisplay.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }
              ),
              Status: node.data.status || "",
              "Approval Status": node.data.approval_status || "",
              "Payment Status": node.data.allocationStatus?.label || "",
              "Display Currency": displayCurrency,
            };

            // Add original USD values if converted
            if (isConverted) {
              exportRow["Total Amount (USD)"] = totalUSD.toLocaleString(
                "en-US",
                { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
              );
              exportRow["Paid Amount (USD)"] = paidUSD.toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              });
              exportRow[
                "Exchange Rate"
              ] = `1 USD = ${exchangeRate} ${displayCurrency}`;
            }

            invoicesToExport.push(exportRow);
          }
        }
      }

      if (invoicesToExport.length === 0) {
        message.warning("No invoices to export (check your filters)");
        return;
      }

      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      const currencySuffix = isConverted ? `_${displayCurrency}` : "";
      secureExportToExcel(
        invoicesToExport,
        `invoices_export${currencySuffix}_${timestamp}.xlsx`
      );
      message.success(
        `Exported ${invoicesToExport.length} invoices successfully!`
      );
    } catch (error) {
      console.error("Error exporting invoices to Excel:", error);
      message.error("Failed to export invoices: " + error.message);
    }
  }, [isConverted, exchangeRate, displayCurrency]);

  // Export credit notes to Excel - directly downloads, respects AG Grid filters
  // Export credit notes to Excel - directly downloads, respects AG Grid filters
  // Includes currency conversion when a non-USD currency is selected
  const exportCreditsToExcel = useCallback(() => {
    try {
      const creditsToExport = [];
      const creditsApi = creditsGridApiRef.current;

      if (creditsApi) {
        const displayedCreditCount = creditsApi.getDisplayedRowCount();
        for (let i = 0; i < displayedCreditCount; i++) {
          const node = creditsApi.getDisplayedRowAtIndex(i);
          if (node && node.data) {
            const totalUSD = parseFloat(node.data.total || 0);
            const allocatedUSD = parseFloat(node.data.allocated_amount || 0);
            const remainingUSD = parseFloat(node.data.remaining_amount || 0);

            // Apply currency conversion if active
            const totalDisplay = isConverted
              ? totalUSD * exchangeRate
              : totalUSD;
            const allocatedDisplay = isConverted
              ? allocatedUSD * exchangeRate
              : allocatedUSD;
            const remainingDisplay = isConverted
              ? remainingUSD * exchangeRate
              : remainingUSD;

            const exportRow = {
              "Credit Number": node.data.number || "",
              Client: node.data.clientName || "N/A",
              Brand: node.data.brandName || "N/A",
              Customer: node.data.partner || "",
              "Issue Date": node.data.issueDate
                ? dayjs(node.data.issueDate).format("YYYY-MM-DD")
                : "",
              [`Total Amount (${displayCurrency})`]:
                totalDisplay.toLocaleString("en-US", {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }),
              [`Allocated Amount (${displayCurrency})`]:
                allocatedDisplay.toLocaleString("en-US", {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }),
              [`Remaining Amount (${displayCurrency})`]:
                remainingDisplay.toLocaleString("en-US", {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }),
              Status: node.data.status || "",
              "Approval Status": node.data.approval_status || "",
              "Allocation Status": node.data.allocationStatus?.label || "",
              "Display Currency": displayCurrency,
            };

            // Add original USD values if converted
            if (isConverted) {
              exportRow["Total Amount (USD)"] = totalUSD.toLocaleString(
                "en-US",
                { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
              );
              exportRow["Allocated Amount (USD)"] = allocatedUSD.toLocaleString(
                "en-US",
                { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
              );
              exportRow["Remaining Amount (USD)"] = remainingUSD.toLocaleString(
                "en-US",
                { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
              );
              exportRow[
                "Exchange Rate"
              ] = `1 USD = ${exchangeRate} ${displayCurrency}`;
            }

            creditsToExport.push(exportRow);
          }
        }
      }

      if (creditsToExport.length === 0) {
        message.warning("No credit notes to export (check your filters)");
        return;
      }

      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      const currencySuffix = isConverted ? `_${displayCurrency}` : "";
      secureExportToExcel(
        creditsToExport,
        `credit_notes_export${currencySuffix}_${timestamp}.xlsx`
      );
      message.success(
        `Exported ${creditsToExport.length} credit notes successfully!`
      );
    } catch (error) {
      console.error("Error exporting credits to Excel:", error);
      message.error("Failed to export credit notes: " + error.message);
    }
  }, [isConverted, exchangeRate, displayCurrency]);

  // Cell editing event handlers
  const onCellValueChanged = (params) => {
    // Update the data state with the new value
    const newData = [...data];
    const rowIndex = newData.findIndex((row) => row.id === params.data.id);
    if (rowIndex !== -1) {
      newData[rowIndex] = {
        ...newData[rowIndex],
        [params.colDef.field]: params.newValue,
      };
      setData(newData);
    }
  };

  // AG Grid column definitions are now derived from userRole defined at the top.

  // Refresh data when userRole changes (e.g., when switching accounts)
  useEffect(() => {
    if (brandsKey && userRole) {
      // Small delay to ensure profile is fully loaded
      const timer = setTimeout(() => {
        fetchInvoicesData();
        fetchCreditNotesData();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [userRole, brandsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const invoiceColumns = useMemo(
    () => getInvoiceColumns({}, userRole, data),
    [userRole, data]
  );
  const creditNoteColumns = useMemo(
    () => getCreditNoteColumns({}, userRole, creditNotesData),
    [userRole, creditNotesData]
  );
  const historyColumns = useMemo(() => getHistoryColumns(userRole), [userRole]);

  // AG Grid context to pass handlers to cell renderers
  const gridContext = useMemo(
    () => ({
      handleViewInvoice,
      handleEditInvoice,
      handleDeleteInvoice,
      handleExportPDF,
      handleViewCredit,
      handleEditCredit,
      handleDeleteCredit,
      handleExportCreditPDF,
      handleViewPayments,
      handleApproveInvoice,
      handleRejectInvoice,
      handleApproveCredit,
      handleRejectCredit,
      handleOpenApprovalModal,
      handleToggleInvoiceSelection,
      handleToggleCreditSelection,
      selectedInvoiceIds,
      selectedCreditIds,
      userRole,
    }),
    [
      handleViewInvoice,
      handleViewPayments,
      handleEditInvoice,
      handleDeleteInvoice,
      handleExportPDF,
      handleExportCreditPDF,
      handleViewCredit,
      handleEditCredit,
      handleDeleteCredit,
      handleApproveInvoice,
      handleRejectInvoice,
      handleApproveCredit,
      handleRejectCredit,
      handleOpenApprovalModal,
      handleToggleInvoiceSelection,
      handleToggleCreditSelection,
      selectedInvoiceIds,
      selectedCreditIds,
      userRole,
    ]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      editable: false,
      suppressMovable: false, // Allow column moving
      // Enable AND/OR conditions for text filters
      filterParams: {
        suppressAndOrCondition: false,
        buttons: ["apply", "reset"],
      },
    }),
    []
  );

  const onInvoicesGridReady = useCallback((params) => {
    invoicesGridApiRef.current = params.api;
  }, []);

  const onCreditsGridReady = useCallback((params) => {
    creditsGridApiRef.current = params.api;
  }, []);

  // Resize Actions column when data changes to reflect dynamic width
  useEffect(() => {
    if (invoicesGridApiRef.current && invoiceColumns) {
      const actionsCol = invoiceColumns.find((col) => col.field === "actions");
      if (actionsCol && actionsCol.width) {
        // Small delay to ensure columns are updated
        setTimeout(() => {
          try {
            if (invoicesGridApiRef.current) {
              const column = invoicesGridApiRef.current.getColumn("actions");
              if (
                column &&
                typeof invoicesGridApiRef.current.setColumnWidth === "function"
              ) {
                invoicesGridApiRef.current.setColumnWidth(
                  "actions",
                  actionsCol.width,
                  true
                );
              }
            }
          } catch (error) {
            console.warn(
              "Error setting column width for invoices grid:",
              error
            );
          }
        }, 100);
      }
    }
  }, [invoiceColumns, data]);

  useEffect(() => {
    if (creditsGridApiRef.current && creditNoteColumns) {
      const actionsCol = creditNoteColumns.find(
        (col) => col.field === "actions"
      );
      if (actionsCol && actionsCol.width) {
        // Small delay to ensure columns are updated
        setTimeout(() => {
          try {
            if (creditsGridApiRef.current) {
              const column = creditsGridApiRef.current.getColumn("actions");
              if (
                column &&
                typeof creditsGridApiRef.current.setColumnWidth === "function"
              ) {
                creditsGridApiRef.current.setColumnWidth(
                  "actions",
                  actionsCol.width,
                  true
                );
              }
            }
          } catch (error) {
            console.warn("Error setting column width for credits grid:", error);
          }
        }, 100);
      }
    }
  }, [creditNoteColumns, creditNotesData]);

  // Dashboard Tab
  const DashboardTabContent = useMemo(() => {
    const { start: periodStart, end: periodEnd } =
      getDateRangeFromFilter(dashboardDateFilter);

    return (
      <div>
        {/* Date Filter Controls */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <strong>Period Filter:</strong>
                <Select
                  style={{ width: "100%" }}
                  value={dashboardDateFilter}
                  onChange={(value) => {
                    setDashboardDateFilter(value);
                    if (value !== "custom") {
                      setDashboardCustomRange(null);
                    }
                  }}
                >
                  <Option value="this_month">This Month</Option>
                  <Option value="last_month">Last Month</Option>
                  <Option value="this_year">This Year (YTD)</Option>
                  <Option value="custom">Custom Range</Option>
                </Select>
              </Space>
            </Col>
            {dashboardDateFilter === "custom" && (
              <Col xs={24} sm={12} md={8}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <strong>Custom Date Range:</strong>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={dashboardCustomRange}
                    onChange={setDashboardCustomRange}
                    format="YYYY-MM-DD"
                  />
                </Space>
              </Col>
            )}
            <Col xs={24} sm={12} md={8}>
              <div style={{ color: "#666", fontSize: 12 }}>
                <strong>Selected Period:</strong>
                <br />
                {periodStart.format("MMM DD, YYYY")} -{" "}
                {periodEnd.format("MMM DD, YYYY")}
              </div>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Unpaid Balance"
                value={
                  isConverted
                    ? statistics.totalOutstanding * exchangeRate
                    : statistics.totalOutstanding
                }
                precision={decimalPrecision}
                valueStyle={{ color: "#1890ff" }}
                prefix={
                  isConverted ? (
                    <>
                      <DollarOutlined />{" "}
                      <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                    </>
                  ) : (
                    <DollarOutlined />
                  )
                }
                suffix={displayCurrency}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                Current remaining (all time)
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Issued (Period)"
                value={
                  isConverted
                    ? (statistics.issuedInPeriod || 0) * exchangeRate
                    : statistics.issuedInPeriod || 0
                }
                precision={decimalPrecision}
                valueStyle={{ color: "#722ed1" }}
                prefix={
                  isConverted ? (
                    <>
                      <FileTextOutlined />{" "}
                      <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                    </>
                  ) : (
                    <FileTextOutlined />
                  )
                }
                suffix={displayCurrency}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                Invoices issued in selected period
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Collected (Period)"
                value={
                  isConverted
                    ? statistics.collectedThisMonth * exchangeRate
                    : statistics.collectedThisMonth
                }
                precision={decimalPrecision}
                valueStyle={{ color: "#3f8600" }}
                prefix={
                  isConverted ? (
                    <>
                      <CheckCircleOutlined />{" "}
                      <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                    </>
                  ) : (
                    <CheckCircleOutlined />
                  )
                }
                suffix={displayCurrency}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                Payments made in period
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Invoices"
                value={statistics.totalInvoices}
                precision={0}
                valueStyle={{ color: "#1890ff" }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Credit Notes"
                value={statistics.totalCredits}
                precision={0}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Unallocated Credits"
                value={statistics.unallocatedCredits}
                precision={0}
                valueStyle={{ color: "#faad14" }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Pending Payments"
                value={statistics.pendingPayments}
                precision={0}
                valueStyle={{ color: "#722ed1" }}
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Recent Invoices" style={{ marginBottom: 16 }}>
          <div
            className="ag-theme-alpine"
            style={{ height: 300, width: "100%" }}
          >
            <AgGridWithColumnState
              key={`recent-invoices-${currencyRefreshKey}`}
              tableId="accounts-receivable-recent-invoices"
              columnDefs={invoiceColumns}
              rowData={data.slice(0, 5)}
              defaultColDef={defaultColDef}
              context={gridContext}
              domLayout="normal"
              stopEditingWhenCellsLoseFocus={true}
              singleClickEdit={true}
              onCellValueChanged={onCellValueChanged}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              initialState={{
                sort: {
                  sortModel: [{ colId: "issueDate", sort: "desc" }],
                },
              }}
            />
          </div>
        </Card>

        <Card title="Recent Credit Notes">
          <div
            className="ag-theme-alpine"
            style={{ height: 300, width: "100%" }}
          >
            <AgGridWithColumnState
              key={`recent-credits-${currencyRefreshKey}`}
              tableId="accounts-receivable-recent-credits"
              columnDefs={creditNoteColumns}
              rowData={creditNotesData.slice(0, 5)}
              defaultColDef={defaultColDef}
              context={gridContext}
              domLayout="normal"
              stopEditingWhenCellsLoseFocus={true}
              singleClickEdit={true}
              onCellValueChanged={onCellValueChanged}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              initialState={{
                sort: {
                  sortModel: [{ colId: "issueDate", sort: "desc" }],
                },
              }}
            />
          </div>
        </Card>
      </div>
    );
  }, [
    dashboardDateFilter,
    dashboardCustomRange,
    statistics,
    isConverted,
    exchangeRate,
    displayCurrency,
    invoiceColumns,
    creditNoteColumns,
    data,
    creditNotesData,
    defaultColDef,
    gridContext,
    currencyRefreshKey,
    onCellValueChanged,
  ]);

  // CRITICAL: Memoize tab content to prevent unnecessary re-creation and glitching
  // These were previously function components that caused remounting on every parent render
  const InvoicesTabContent = useMemo(() => {
    const hasSelectedEligibleInvoices = eligibleInvoiceCount > 0;
    const invoiceButtonText = hasSelectedEligibleInvoices
      ? `Approve Selected (${eligibleInvoiceCount})`
      : `Approve All (${totalApprovableInvoices})`;

    const hasSelectedEligibleCredits = eligibleCreditCount > 0;
    const creditButtonText = hasSelectedEligibleCredits
      ? `Approve Selected (${eligibleCreditCount})`
      : `Approve All (${totalApprovableCredits})`;

    const normalizedUserRole = userRole?.toLowerCase();
    const showInvoiceApproveButton =
      normalizedUserRole === "management" &&
      (totalApprovableInvoices > 0 || hasSelectedEligibleInvoices);
    const showCreditApproveButton =
      normalizedUserRole === "management" &&
      (totalApprovableCredits > 0 || hasSelectedEligibleCredits);

    return (
      <div>
        <Card style={{ marginBottom: 16 }}>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <h4>Invoices</h4>
            <Space>
              {showInvoiceApproveButton && (
                <Button
                  type="default"
                  onClick={() =>
                    handleBulkApprove("invoice", hasSelectedEligibleInvoices)
                  }
                  loading={bulkApproving}
                  disabled={bulkApproving}
                >
                  {invoiceButtonText}
                </Button>
              )}
              <Button
                onClick={exportInvoicesToExcel}
                icon={
                  <ImageWithBasePath
                    src="assets/img/icons/xls.svg"
                    alt="Excel"
                    style={{ width: 16, height: 16 }}
                  />
                }
                title="Export Invoices to Excel"
              />
              <Button
                type="primary"
                onClick={() => setBatchUploadInvoicesVisible(true)}
                icon={<UploadOutlined />}
              >
                Upload Invoices
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateInvoiceModalVisible(true)}
              >
                Create New Invoice
              </Button>
            </Space>
          </div>

          <ResizableGridContainer defaultHeight={700} minHeight={300} maxHeight={1400}>
            <AgGridWithColumnState
              key={`invoices-grid-${currencyRefreshKey}`}
              tableId="accounts-receivable-invoices"
              columnDefs={invoiceColumns}
              rowData={data}
              defaultColDef={defaultColDef}
              context={gridContext}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              gridApiRef={invoicesGridApiRef}
              getRowId={(params) => String(params.data.id)}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              stopEditingWhenCellsLoseFocus={true}
              singleClickEdit={true}
              onCellValueChanged={onCellValueChanged}
              onSelectionChanged={handleInvoiceSelectionChanged}
              domLayout="normal"
              suppressScrollOnNewData={true}
              animateRows={false}
              rowBuffer={10}
            />
          </ResizableGridContainer>
          {selectedInvoiceIds.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDeleteInvoices}
              >
                Delete Selected ({selectedInvoiceIds.length})
              </Button>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setBatchEditModalVisible(true)}
              >
                Batch Edit ({selectedInvoiceIds.length})
              </Button>
            </div>
          )}
        </Card>

        <Card>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <h4>Credit Notes</h4>
            <Space>
              {showCreditApproveButton && (
                <Button
                  type="default"
                  onClick={() =>
                    handleBulkApprove("credit", hasSelectedEligibleCredits)
                  }
                  loading={bulkApproving}
                  disabled={bulkApproving}
                >
                  {creditButtonText}
                </Button>
              )}
              <Button
                onClick={exportCreditsToExcel}
                icon={
                  <ImageWithBasePath
                    src="assets/img/icons/xls.svg"
                    alt="Excel"
                    style={{ width: 16, height: 16 }}
                  />
                }
                title="Export Credit Notes to Excel"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateCreditModalVisible(true)}
              >
                Create Credit Note
              </Button>
            </Space>
          </div>

          <ResizableGridContainer defaultHeight={700} minHeight={300} maxHeight={1400}>
            <AgGridWithColumnState
              key={`credits-grid-${currencyRefreshKey}`}
              tableId="accounts-receivable-credits"
              columnDefs={creditNoteColumns}
              rowData={creditNotesData}
              defaultColDef={defaultColDef}
              context={gridContext}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              gridApiRef={creditsGridApiRef}
              getRowId={(params) => String(params.data.id)}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              stopEditingWhenCellsLoseFocus={true}
              singleClickEdit={true}
              onCellValueChanged={onCellValueChanged}
              onSelectionChanged={handleCreditSelectionChanged}
              domLayout="normal"
              suppressScrollOnNewData={true}
              animateRows={false}
              rowBuffer={10}
            />
          </ResizableGridContainer>
          {selectedCreditIds.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDeleteCredits}
              >
                Delete Selected ({selectedCreditIds.length})
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  }, [
    data,
    creditNotesData,
    invoiceColumns,
    creditNoteColumns,
    defaultColDef,
    gridContext,
    onCellValueChanged,
    exportInvoicesToExcel,
    exportCreditsToExcel,
    showUploadModal,
    handleBulkApprove,
    bulkApproving,
    eligibleInvoiceCount,
    eligibleCreditCount,
    totalApprovableInvoices,
    totalApprovableCredits,
    userRole,
    selectedInvoiceIds,
    selectedCreditIds,
    handleBulkDeleteInvoices,
    handleBulkDeleteCredits,
    handleInvoiceSelectionChanged,
    handleCreditSelectionChanged,
  ]);

  const HistoryTabContent = useMemo(
    () => (
      <div>
        <Card title="Invoice Action History">
          <InvoiceHistory
            firstBrand={firstBrand}
            selectedBrands={selectedBrands}
          />
        </Card>
      </div>
    ),
    [firstBrand, selectedBrands]
  );

  const UpcomingInvoicesTabContent = useMemo(
    () => (
      <div>
        <UpcomingInvoices
          firstBrand={firstBrand}
          selectedBrands={selectedBrands}
        />
      </div>
    ),
    [firstBrand, selectedBrands]
  );

  // Tab items configuration
  // CRITICAL: We keep destroyInactiveTabPane=false and forceRender=true for the invoices tab
  // to ensure the AG Grid stays mounted and preserves filter state between actions
  const tabItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      children: DashboardTabContent,
    },
    {
      key: "invoices",
      label: "Invoices and Credit notes",
      children: InvoicesTabContent,
      forceRender: true, // Keep grid mounted to preserve filter state
    },
    {
      key: "history",
      label: "Invoice History",
      children: HistoryTabContent,
    },
    {
      key: "upcoming",
      label: "Upcoming Invoices",
      children: UpcomingInvoicesTabContent,
    },
  ];

  return (
    <div style={{ padding: "16px", width: "100%", minHeight: "100vh" }}>
      <h2 style={{ margin: "0 0 16px 0", fontSize: "24px", fontWeight: "600" }}>
        Accounts Receivable
      </h2>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          localStorage.setItem("accounts-receivable-active-tab", key);
        }}
        items={tabItems}
        type="card"
        destroyInactiveTabPane={false}
      />

      {/* Create/Edit Invoice Modal - only mount when needed */}
      {createInvoiceModalVisible && (
        <CreateInvoiceModal
          visible={createInvoiceModalVisible}
          onCancel={() => {
            setCreateInvoiceModalVisible(false);
            setEditingInvoiceId(null);
          }}
          onSuccess={handleCreateInvoiceSuccess}
          selectedBrand={firstBrand}
          invoiceId={editingInvoiceId}
        />
      )}

      {/* Batch Upload Invoices Modal */}
      <BatchUploadBillsInvoices
        visible={batchUploadInvoicesVisible}
        onCancel={() => setBatchUploadInvoicesVisible(false)}
        onSuccess={handleRefresh}
        selectedBrand={firstBrand}
        invoiceType="invoice"
      />

      {/* Batch Edit Modal */}
      <BatchEditModal
        visible={batchEditModalVisible}
        onCancel={() => setBatchEditModalVisible(false)}
        onSuccess={() => {
          setBatchEditModalVisible(false);
          setSelectedInvoiceIds([]);
          handleRefresh();
        }}
        selectedIds={selectedInvoiceIds}
        invoiceType="invoice"
        currentBrand={firstBrand}
      />

      {/* Create/Edit Credit Modal */}
      <Modal
        title={editingCreditId ? "Edit Credit Note" : "Create Credit Note"}
        open={createCreditModalVisible}
        onCancel={() => {
          setCreateCreditModalVisible(false);
          setEditingCreditId(null);
        }}
        footer={null}
        width="95%"
        style={{ top: 20 }}
        zIndex={2000}
        destroyOnClose
      >
        <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <CreateCredit
            selectedBrand={firstBrand}
            creditId={editingCreditId}
            onSuccess={handleCreateCreditSuccess}
            defaultCreditType="invoice"
          />
        </div>
      </Modal>

      {/* Upload Invoices Modal (legacy) */}
      <Modal
        title="Upload Invoices File"
        open={uploadModalVisible}
        onOk={handleUploadOk}
        onCancel={handleUploadCancel}
        confirmLoading={uploadLoading}
        okText="Upload"
        zIndex={2000}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="file"
            label="Excel or CSV File"
            rules={[{ required: true, message: "Please upload a file" }]}
          >
            <Upload.Dragger {...uploadProps} maxCount={1}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Only Excel (.xlsx, .xls) and CSV (.csv) files are supported
              </p>
              <p
                className="ant-upload-hint"
                style={{
                  marginTop: "8px",
                  fontWeight: "bold",
                  color: "#1890ff",
                }}
              >
                Uploading as: Invoice
              </p>
            </Upload.Dragger>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea
              rows={4}
              placeholder="Add any notes about this invoices file"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Invoice View Modal */}
      <InvoiceViewModal
        visible={viewInvoiceModalVisible}
        onCancel={() => {
          setViewInvoiceModalVisible(false);
          setViewingInvoiceId(null);
          setViewingCreditId(null);
        }}
        invoiceId={viewingInvoiceId || viewingCreditId}
        invoiceType={viewingCreditId ? "credit" : "invoice"}
        isCredit={!!viewingCreditId}
      />

      {/* Approval Modal */}
      <ApprovalModal
        visible={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false);
          setSelectedItemForApproval(null);
        }}
        onSuccess={handleApprovalSuccess}
        item={selectedItemForApproval}
        itemType={approvalItemType}
        userRole={userRole}
      />

      {/* Allocations Modal */}
      <Modal
        title={`${
          selectedInvoiceForPayments?.allocations?.length || 0
        } Allocation${
          (selectedInvoiceForPayments?.allocations?.length || 0) > 1 ? "s" : ""
        }`}
        open={paymentsModalVisible}
        onCancel={() => {
          setPaymentsModalVisible(false);
          setSelectedInvoiceForPayments(null);
        }}
        footer={null}
        width={800}
      >
        {selectedInvoiceForPayments && (
          <div>
            <p style={{ marginBottom: 16, fontSize: 14 }}>
              <strong>
                Allocations for{" "}
                {selectedInvoiceForPayments.credit_type
                  ? "Credit Note"
                  : "Invoice"}{" "}
                #{selectedInvoiceForPayments.number}
              </strong>
            </p>
            <Table
              dataSource={selectedInvoiceForPayments.allocations || []}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                {
                  title: "Type",
                  dataIndex: "allocation_type",
                  key: "allocation_type",
                  width: 100,
                  render: (type) => {
                    const typeConfig = {
                      payment: { color: "blue", label: "Payment" },
                      credit: { color: "green", label: "Credit" },
                      cash: { color: "purple", label: "Cash" },
                      overpayment: { color: "orange", label: "Overpayment" },
                    };
                    const config = typeConfig[type] || {
                      color: "default",
                      label: type || "Payment",
                    };
                    return <Tag color={config.color}>{config.label}</Tag>;
                  },
                },
                {
                  title: "Description",
                  dataIndex: ["transaction", "description"],
                  key: "description",
                  render: (text, record) => {
                    // Handle different allocation types
                    let desc = text || record.description || "No description";

                    // For credit allocations, show the invoice number that the credit is allocated to
                    if (record.allocation_type === "credit") {
                      if (record.invoice_number) {
                        desc = `Invoice #${record.invoice_number}`;
                      } else if (record.invoice?.number) {
                        desc = `Invoice #${record.invoice.number}`;
                      } else if (record.credit) {
                        desc = `Credit Note #${
                          record.credit.number || record.credit.id
                        }`;
                      }
                    }
                    // For cash allocations, show cash number
                    else if (record.allocation_type === "cash" && record.cash) {
                      desc = `Cash Payment #${
                        record.cash.number || record.cash.id
                      }`;
                    }
                    // For overpayment, show overpayment info
                    else if (
                      record.allocation_type === "overpayment" &&
                      record.overpayment
                    ) {
                      desc = record.overpayment.description || "Overpayment";
                    }

                    // Make clickable for transactions
                    if (
                      record.allocation_type === "payment" &&
                      (record.transaction || record.transaction_id)
                    ) {
                      // Extract actual transaction ID - handle both object and primitive
                      const rawTransaction =
                        record.transaction_id || record.transaction;
                      const transactionId =
                        typeof rawTransaction === "object" &&
                        rawTransaction !== null
                          ? rawTransaction.id
                          : rawTransaction;

                      if (transactionId) {
                        return (
                          <a
                            href={`/accounting/transactions-allocations?transaction=${transactionId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              // Use navigate for SPA navigation instead of full page reload
                              navigate(
                                `/accounting/transactions-allocations?transaction=${transactionId}`
                              );
                            }}
                            style={{ color: "#1890ff", cursor: "pointer" }}
                          >
                            {desc}
                          </a>
                        );
                      }
                    }
                    return desc;
                  },
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  width: 120,
                  render: (amount) =>
                    `${selectedInvoiceForPayments.currency || "$"} ${parseFloat(
                      amount || 0
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: decimalPrecision,
                      maximumFractionDigits: decimalPrecision,
                    })}`,
                },
                {
                  title: "Date",
                  dataIndex: "allocation_date",
                  key: "allocation_date",
                  width: 120,
                  render: (date, record) => {
                    // Use transaction date for payment allocations, otherwise allocation date
                    const displayDate =
                      record.allocation_type === "payment" &&
                      record.transaction_date
                        ? record.transaction_date
                        : date;
                    return displayDate
                      ? dayjs(displayDate).format("MM/DD/YYYY")
                      : "N/A";
                  },
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AccountsReceivable;
